import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Shield, Eye, EyeOff, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex font-nunito" data-testid="login-page">
      {/* Left side - decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/43b86a66-f576-4564-b06d-193403b5349b/images/f2ace498661f24599a2029b74ace8557955807e6821093bc0edf821d16d31a9d.png"
          alt="Alfie"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 text-center px-12">
          <div className="inline-flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-[32px] px-8 py-5 shadow-lg">
            <Shield className="w-10 h-10 text-[#FF5C39]" strokeWidth={3} />
            <span className="text-3xl font-extrabold text-[#1F1F2E]">Alfie</span>
          </div>
          <p className="mt-6 text-lg font-bold text-white bg-[#1F1F2E]/70 backdrop-blur-sm rounded-2xl px-6 py-3 inline-block">
            Keeping kids safe, giving parents peace of mind
          </p>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-[#FF5C39] rounded-2xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <span className="text-2xl font-extrabold text-[#1F1F2E]">Alfie</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-extrabold text-[#1F1F2E] tracking-tight" data-testid="login-heading">
            Welcome back
          </h1>
          <p className="text-[#52526B] font-semibold mt-2 mb-8">
            Sign in to check on your little one
          </p>

          {error && (
            <div className="bg-[#F43F5E]/10 text-[#F43F5E] px-4 py-3 rounded-2xl font-bold text-sm mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50"
                placeholder="parent@email.com"
                required
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 pr-12 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50"
                  placeholder="Your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52526B]"
                >
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-sm font-bold text-[#4D96FF] hover:text-[#FF5C39] transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-4 hover:bg-[#E04828] active:scale-95 transition-all shadow-[0_8px_20px_rgba(255,92,57,0.25)] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Sign in
            </button>
          </form>

          <p className="mt-8 text-center text-[#52526B] font-semibold text-sm">
            New to Alfie?{" "}
            <Link to="/signup" data-testid="signup-link" className="text-[#FF5C39] font-bold hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
